import { useCallback, useEffect, useRef, useState } from "react";
import {
  taskEditorAiApi,
  type TaskEditorAIGenerateStatus as APIStatus,
} from "api/taskEditorAiApi";
import type { TaskEditorState } from "./taskEditorUtils";
import { isTaskEditorState } from "./taskEditorRuntime";
import type { AIGenerateRequest } from "./TaskEditorAIGenerateModal";

const POLL_INTERVAL_MS = 2000;

export type AIGenerateStatus =
  | { type: "idle" }
  | { type: "generating"; startedAt: number; requestId: string; progress?: number }
  | { type: "completed"; result: TaskEditorState }
  | { type: "error"; message: string };

export function useTaskEditorAIGenerate() {
  const [status, setStatus] = useState<AIGenerateStatus>({ type: "idle" });
  const abortRef = useRef(false);
  const pollTimerRef = useRef<number | undefined>(undefined);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current !== undefined) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = undefined;
    }
  }, []);

  const pollStatus = useCallback(
    async (requestId: string, startedAt: number) => {
      if (abortRef.current) {
        return;
      }

      try {
        const statusResponse = await taskEditorAiApi.getStatus(requestId);
        const statusData = statusResponse.data;

        if (abortRef.current) {
          return;
        }

        if (!statusData) {
          setStatus({ type: "error", message: "상태를 확인할 수 없습니다." });
          return;
        }

        const apiStatus: APIStatus = statusData.status;

        if (apiStatus === "completed") {
          const resultResponse = await taskEditorAiApi.getResult(requestId);
          const result = resultResponse.data;

          if (abortRef.current) {
            return;
          }

          if (!result || !isTaskEditorState(result)) {
            setStatus({
              type: "error",
              message: "생성된 결과를 파싱할 수 없습니다.",
            });
            return;
          }

          setStatus({ type: "completed", result });
          return;
        }

        if (apiStatus === "failed") {
          setStatus({
            type: "error",
            message: statusData.errorMessage || "생성에 실패했습니다.",
          });
          return;
        }

        // pending or processing → update progress, keep polling
        setStatus({
          type: "generating",
          startedAt,
          requestId,
          progress: statusData.progress,
        });

        pollTimerRef.current = window.setTimeout(() => {
          pollStatus(requestId, startedAt);
        }, POLL_INTERVAL_MS);
      } catch (e) {
        if (abortRef.current) {
          return;
        }

        setStatus({
          type: "error",
          message:
            e instanceof Error
              ? e.message
              : "상태 확인 중 오류가 발생했습니다.",
        });
      }
    },
    []
  );

  const startGenerate = useCallback(
    async (request: AIGenerateRequest) => {
      abortRef.current = false;
      stopPolling();

      const startedAt = Date.now();
      setStatus({ type: "generating", startedAt, requestId: "" });

      try {
        const response = await taskEditorAiApi.generate({
          rawText: request.rawText,
          additionalInstructions: request.additionalInstructions || undefined,
          previousResult: request.previousResult,
        });

        if (abortRef.current) {
          setStatus({ type: "idle" });
          return;
        }

        const requestId = response.data?.requestId;

        if (!requestId) {
          setStatus({
            type: "error",
            message: "생성 요청 ID를 받지 못했습니다.",
          });
          return;
        }

        setStatus({ type: "generating", startedAt, requestId });

        // Start polling
        pollTimerRef.current = window.setTimeout(() => {
          pollStatus(requestId, startedAt);
        }, POLL_INTERVAL_MS);
      } catch (e) {
        if (abortRef.current) {
          setStatus({ type: "idle" });
          return;
        }

        setStatus({
          type: "error",
          message:
            e instanceof Error
              ? e.message
              : "생성 요청 중 오류가 발생했습니다.",
        });
      }
    },
    [stopPolling, pollStatus]
  );

  const dismiss = useCallback(() => {
    abortRef.current = true;
    stopPolling();
    setStatus({ type: "idle" });
  }, [stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current = true;
      stopPolling();
    };
  }, [stopPolling]);

  return { status, startGenerate, dismiss };
}
